import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { Typography as T } from "@mui/material";
import { DataDistribution } from "../../models/datasource";
import { StyledDataGrid } from "../../components/common";
import { compactFormatter } from "../../utils/numberFormatter";

type CompletenessKey = keyof DataDistribution["completeness_percentage"];

const PercentageCell = ({ value }: { value?: number }): JSX.Element => {
  const output = value != null ? `${compactFormatter.format(value)}%` : "-";
  return (
    <div>
      <T display="inline" fontSize={14}>
        {output}
      </T>
    </div>
  );
};

type DataCompletenessTableProps = {
  data: DataDistribution[];
};

const DataCompletenessTable = ({ data }: DataCompletenessTableProps): JSX.Element => {
  // Completeness percentages are nested under `completeness_percentage`, so each column
  // needs a valueGetter for the DataGrid to read (and therefore sort by) the value.
  const percentageColumn = (
    field: CompletenessKey,
    headerName: string,
    minWidth: number,
  ): GridColDef<DataDistribution> => ({
    field,
    headerName,
    flex: 1,
    minWidth,
    valueGetter: (params: GridValueGetterParams<unknown, DataDistribution>) =>
      params.row.completeness_percentage[field],
    renderCell: (params) => <PercentageCell value={params.value as number | undefined} />,
  });

  const columns: GridColDef<DataDistribution>[] = [
    {
      field: "name",
      headerName: "Source",
      flex: 3,
      minWidth: 320,
      renderCell: (params) => (
        <T
          fontWeight={600}
          fontSize={14}
          sx={{
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
          }}
        >
          {params.row.name}
        </T>
      ),
    },
    percentageColumn("date", "Date", 100),
    percentageColumn("therapeutic_area", "Therapeutic Area", 120),
    percentageColumn("product_molecule", "Product/Molecule", 140),
    percentageColumn("indication", "Indication", 120),
    percentageColumn("ecosystem_name", "Ecosystem", 120),
    percentageColumn("corp_account", "Corp Account", 130),
    percentageColumn("account_type", "Account Type", 130),
    percentageColumn("role_type", "Role Type", 120),
  ];

  // have to augment the data rows with an ID value; for now, name of datasource.
  return (
    <StyledDataGrid
      autoHeight
      rows={data.map((d) => ({ ...d, id: d.name }))}
      columns={columns}
      hideFooter
      disableColumnMenu
    />
  );
};

export default DataCompletenessTable;
